import { useCallback, useEffect, useState } from "react";
import { ImageIcon, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl } from "@/lib/api";
import { fmtIDR } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import SquareCropModal from "@/components/SquareCropModal";

const CUR_SYM = { JPY: "¥", KRW: "₩", EUR: "€", HKD: "HK$", AUD: "A$", USD: "$" };
const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();

export default function Masters() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [canon, setCanon] = useState("");
  const [aliases, setAliases] = useState([]);
  const [aliasInput, setAliasInput] = useState("");
  const [defLoc, setDefLoc] = useState("");
  const [photoPath, setPhotoPath] = useState("");
  const [locations, setLocations] = useState([]);
  const [crop, setCrop] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("active");
  const [inUse, setInUse] = useState(false);
  const [dangerMode, setDangerMode] = useState(null); // null | "delete" | "archive" | "restore"
  const [deleteText, setDeleteText] = useState("");
  const [dangerBusy, setDangerBusy] = useState(false);

  const load = useCallback(async (query, whichTab) => {
    const useTab = whichTab ?? tab;
    try {
      const params = {};
      if (query) params.q = query;
      if (useTab === "archived") params.archived = 1;
      const { data } = await api.get("/masters", { params });
      setResults(data.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q, load]);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(Array.isArray(r.data) ? r.data : r.data.locations || [])).catch(() => {});
  }, []);

  const openDetail = (m) => {
    setDetail(m);
    setCanon(m.canonical_name);
    setAliases(m.aliases || []);
    setDefLoc(m.default_location || "");
    setPhotoPath(m.photo_path || "");
    setAliasInput("");
    setDangerMode(null);
    setDeleteText("");
    setInUse(true); // safe default until fresh usage flag arrives
    api.get(`/masters/${m.id}`).then((r) => { setDetail(r.data); setInUse(!!r.data.in_use); }).catch(() => {});
  };

  const addAlias = () => {
    const a = aliasInput.trim();
    if (!a) return;
    if (norm(a).length < 3) {
      toast.error("Alias too short to be meaningful");
      return;
    }
    if (norm(a) === norm(canon) || aliases.some((x) => norm(x) === norm(a))) {
      toast.error("Alias duplicates the name or an existing alias");
      return;
    }
    setAliases((prev) => [...prev, a]);
    setAliasInput("");
  };

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
      const c = document.createElement("canvas");
      c.width = bmp.width;
      c.height = bmp.height;
      c.getContext("2d").drawImage(bmp, 0, 0);
      const normBlob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.92));
      if (!normBlob) throw new Error("decode failed");
      setCrop({ url: URL.createObjectURL(normBlob), w: c.width, h: c.height, canvas: c });
    } catch {
      toast.error("Could not read this photo. Please choose a JPEG/PNG image.");
    }
  };

  const onCropConfirm = async (file) => {
    if (crop) URL.revokeObjectURL(crop.url);
    setCrop(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.path);
      toast.success("Photo ready — Save to apply");
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/masters/${detail.id}`, {
        canonical_name: canon,
        aliases,
        default_location: defLoc || null,
        photo_path: photoPath || null,
      });
      toast.success("Master product saved");
      setDetail(data);
      load(q.trim());
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDangerBusy(true);
    try {
      await api.delete(`/masters/${detail.id}`);
      toast.success("Master product permanently deleted");
      setDetail(null);
      setDangerMode(null);
      setDeleteText("");
      load(q.trim());
    } catch (e) {
      if (e?.response?.status === 409) {
        setInUse(true); // backend says it's used — switch to archive offer
        setDangerMode(null);
        setDeleteText("");
      }
      toast.error(apiError(e));
    } finally {
      setDangerBusy(false);
    }
  };

  const doArchive = async () => {
    setDangerBusy(true);
    try {
      const { data } = await api.post(`/masters/${detail.id}/archive`);
      toast.success("Master product archived — history preserved");
      setDetail(data);
      setDangerMode(null);
      load(q.trim());
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDangerBusy(false);
    }
  };

  const doRestore = async () => {
    setDangerBusy(true);
    try {
      const { data } = await api.post(`/masters/${detail.id}/restore`);
      toast.success("Master product restored");
      setDetail(data);
      setDangerMode(null);
      load(q.trim());
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDangerBusy(false);
    }
  };

  const masterImg = (m) => (m.photo_path ? fileUrl(m.photo_path) : m.photo_url || null);

  const lastBought = (m) =>
    m.last_buying_price != null
      ? `${CUR_SYM[m.last_buying_currency] || ""}${Number(m.last_buying_price).toLocaleString("en-US")}${m.last_buying_location ? ` · ${m.last_buying_location}` : ""}`
      : null;

  return (
    <div data-testid="masters-page" className="space-y-4">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">Product Master</h1>
      <Input
        data-testid="masters-search-input"
        className="h-12"
        placeholder="Search canonical name or alias…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="flex gap-2" data-testid="masters-tabs">
        {[
          ["active", "Active"],
          ["archived", "Archived"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-testid={`masters-tab-${key}`}
            onClick={() => setTab(key)}
            className={`h-10 flex-1 rounded-md border text-sm font-bold transition-colors active:scale-[0.98] ${
              tab === key ? "border-primary bg-primary text-white" : "bg-card text-stone-600 hover:bg-accent"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {loading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
      ) : results.length === 0 ? (
        <p data-testid="masters-empty" className="py-8 text-center text-sm text-muted-foreground">
          {q ? "No master products match." : "No master products yet — they are created automatically as products are added."}
        </p>
      ) : (
        <div className="space-y-2" data-testid="masters-list">
          {results.map((m) => (
            <button key={m.id} type="button" data-testid={`master-row-${m.id}`} onClick={() => openDetail(m)} className="flex w-full items-center gap-3 rounded-lg border bg-white p-3 text-left transition-colors hover:bg-accent">
              {masterImg(m) ? (
                <img src={masterImg(m)} alt={m.canonical_name} className="h-12 w-12 shrink-0 rounded-md border object-cover" />
              ) : (
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border bg-[#EDE4DE]">
                  <ImageIcon className="h-5 w-5 text-stone-300" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold">{m.canonical_name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {(m.aliases || []).length > 0 ? `${m.aliases.length} alias(es): ${m.aliases.slice(0, 2).join(", ")}` : "no aliases"}
                  {lastBought(m) ? ` · Last bought ${lastBought(m)}` : ""}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      <Drawer open={!!detail} onOpenChange={(v) => !v && setDetail(null)} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Master Product</DrawerTitle>
          </DrawerHeader>
          {detail && (
            <div className="max-h-[75vh] space-y-4 overflow-y-auto px-4 pb-8" data-testid="master-detail-drawer">
              <div className="flex items-start gap-3">
                {photoPath || detail.photo_url ? (
                  <img src={photoPath ? fileUrl(photoPath) : detail.photo_url} alt={canon} data-testid="master-photo" className="h-24 w-24 rounded-md border object-cover" />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-md border bg-[#EDE4DE]">
                    <ImageIcon className="h-7 w-7 text-stone-300" />
                  </div>
                )}
                <label className="mt-1">
                  <input type="file" accept="image/*" className="hidden" data-testid="master-photo-input" onChange={pickPhoto} />
                  <span data-testid="master-photo-replace" className="inline-flex h-10 cursor-pointer items-center rounded-md border px-3 text-sm font-semibold hover:bg-accent">
                    Replace photo
                  </span>
                </label>
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Canonical name</Label>
                <Input data-testid="master-canonical-input" className="h-12" value={canon} onChange={(e) => setCanon(e.target.value)} />
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Aliases</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="master-alias-chips">
                  {aliases.length === 0 && <p className="text-xs text-muted-foreground">No aliases yet.</p>}
                  {aliases.map((a) => (
                    <span key={a} className="inline-flex items-center gap-1 rounded-full border bg-accent px-2.5 py-1 text-xs font-semibold">
                      {a}
                      <button type="button" data-testid={`master-alias-remove-${a}`} aria-label={`Remove ${a}`} onClick={() => setAliases((prev) => prev.filter((x) => x !== a))}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="master-alias-add-input" className="h-11" placeholder="Add alias…" value={aliasInput} onChange={(e) => setAliasInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAlias())} />
                  <Button type="button" variant="outline" data-testid="master-alias-add-button" className="h-11" onClick={addAlias}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Default buying location</Label>
                <Select value={defLoc || "__none__"} onValueChange={(v) => setDefLoc(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="master-location-select" className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {locations.map((l) => (
                      <SelectItem key={l.id || l.name} value={l.name}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1 rounded-md border bg-accent p-3 text-sm" data-testid="master-reference-info">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Reference (auto-updated on purchase)</p>
                <p>Last bought: {detail.last_buying_price != null ? `${CUR_SYM[detail.last_buying_currency] || ""}${Number(detail.last_buying_price).toLocaleString("en-US")}` : "—"}</p>
                <p>Last location: {detail.last_buying_location || "—"}</p>
                <p>Last purchased: {detail.last_purchased_at ? new Date(detail.last_purchased_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "—"}</p>
                <p>Previous selling price: {detail.last_selling_price != null ? fmtIDR(detail.last_selling_price) : "—"}</p>
                <p>Countries: {(detail.countries || []).join(", ") || "—"}</p>
              </div>

              <Button data-testid="master-save-button" className="h-12 w-full text-base" onClick={save} disabled={saving || !canon.trim()}>
                {saving ? "Saving…" : "Save Master Product"}
              </Button>

              <div className="rounded-md border border-red-200 p-3" data-testid="master-danger-zone">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-red-500">Danger Zone</p>
                {detail.status === "archived" ? (
                  dangerMode === "restore" ? (
                    <div className="space-y-2" data-testid="master-restore-confirm">
                      <p className="text-xs text-stone-600">Restore "{detail.canonical_name}"? It will appear in search and suggestions again.</p>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" className="h-10 flex-1" data-testid="master-restore-cancel" onClick={() => setDangerMode(null)}>
                          Cancel
                        </Button>
                        <Button type="button" className="h-10 flex-1" data-testid="master-restore-confirm-button" disabled={dangerBusy} onClick={doRestore}>
                          {dangerBusy ? "Restoring…" : "Restore Master"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" className="h-10 w-full" data-testid="master-restore-button" onClick={() => setDangerMode("restore")}>
                      Restore Master
                    </Button>
                  )
                ) : inUse ? (
                  dangerMode === "archive" ? (
                    <div className="space-y-2" data-testid="master-archive-confirm">
                      <p className="text-xs text-stone-600">
                        Archive "{detail.canonical_name}"? Existing transaction history will remain unchanged. This product will no longer appear in normal product suggestions.
                      </p>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" className="h-10 flex-1" data-testid="master-archive-cancel" onClick={() => setDangerMode(null)}>
                          Cancel
                        </Button>
                        <Button type="button" className="h-10 flex-1" data-testid="master-archive-confirm-button" disabled={dangerBusy} onClick={doArchive}>
                          {dangerBusy ? "Archiving…" : "Archive Master"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Button type="button" variant="outline" className="h-10 w-full border-amber-300 text-amber-700" data-testid="master-archive-button" onClick={() => setDangerMode("archive")}>
                        Archive Master
                      </Button>
                      <p className="mt-1.5 text-[10px] text-muted-foreground">Used in transactions — permanent delete is blocked. Archiving keeps all history.</p>
                    </>
                  )
                ) : dangerMode === "delete" ? (
                  <div className="space-y-2" data-testid="master-delete-confirm">
                    <p className="text-xs font-semibold text-stone-700">Delete "{detail.canonical_name}"? This Master Product will be permanently deleted.</p>
                    <Input
                      data-testid="master-delete-confirm-input"
                      className="h-11 bg-white"
                      placeholder="Type DELETE to confirm"
                      value={deleteText}
                      onChange={(e) => setDeleteText(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" className="h-10 flex-1" data-testid="master-delete-cancel" onClick={() => { setDangerMode(null); setDeleteText(""); }}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        className="h-10 flex-1"
                        data-testid="master-delete-confirm-button"
                        disabled={dangerBusy || deleteText !== "DELETE"}
                        onClick={doDelete}
                      >
                        {dangerBusy ? "Deleting…" : "Delete Permanently"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button type="button" variant="outline" className="h-10 w-full border-red-300 text-red-600" data-testid="master-delete-button" onClick={() => setDangerMode("delete")}>
                    Delete Master
                  </Button>
                )}
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      <SquareCropModal open={!!crop} imageUrl={crop?.url} natural={crop ? { w: crop.w, h: crop.h } : null} sourceCanvas={crop?.canvas} onConfirm={onCropConfirm} onCancel={() => { if (crop) URL.revokeObjectURL(crop.url); setCrop(null); }} />
    </div>
  );
}
